import { useAuthStore } from '@/store/auth-store';
import { type ComputedRef, computed } from 'vue';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const Permission = {
  AGREEMENT_CREATE: 'agreement-create',
  AGREEMENT_EDIT: 'agreement-edit',
  OMS_ACTIONS: 'oms-actions',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

// eslint-disable-next-line @typescript-eslint/naming-convention
export const UserRole = {
  TRADER: 'trader',
  TRADER_ADMIN: 'trader-admin',
  TRADER_USER: 'trader-user',
  OPS_USER: 'ops-user',
  TRADER_VIEWER: 'trader-viewer',
  CONFIG_ADMIN: 'config-admin',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export function useUserPermissions(): {
  userRoles: ComputedRef<Set<UserRole>>;
  userPermissions: ComputedRef<Set<Permission>>;
  hasPermission: (...permissions: Permission[]) => ComputedRef<boolean>;
} {
  const authStore = useAuthStore();

  const userRoles = computed(() => new Set((authStore.user?.roles as UserRole[]) ?? []));

  const userPermissions = computed(() => {
    const permissions = new Set<Permission>();
    if (userRoles.value.has(UserRole.TRADER)) {
      permissions.add(Permission.OMS_ACTIONS);
    }
    if (userRoles.value.has(UserRole.TRADER_USER)) {
      permissions.add(Permission.OMS_ACTIONS);
    }
    if (userRoles.value.has(UserRole.TRADER_ADMIN)) {
      permissions.add(Permission.AGREEMENT_CREATE);
      permissions.add(Permission.AGREEMENT_EDIT);
      permissions.add(Permission.OMS_ACTIONS);
    }
    if (userRoles.value.has(UserRole.CONFIG_ADMIN)) {
      permissions.add(Permission.AGREEMENT_CREATE);
      permissions.add(Permission.AGREEMENT_EDIT);
    }
    return permissions;
  });

  function hasPermission(...permissions: Permission[]): ComputedRef<boolean> {
    return computed(() => permissions.some((p) => userPermissions.value.has(p)));
  }

  return {
    userRoles,
    userPermissions,
    hasPermission,
  };
}

export function useHasPermission(...permissions: Permission[]): ComputedRef<boolean> {
  const { hasPermission } = useUserPermissions();
  return hasPermission(...permissions);
}
